import { Product, Variant, VariantGroup } from "@/types/types";
import {
  getPriceMatchNonPriceEstablishedStatusForColour,
  getProductPriceMatchNonPriceEstablishedStatus,
  isAllVariantsReduced,
  isAllVariantsReducedForColour,
  isAllVariantsSave,
  isAllVariantsSaveForColour,
  isSomeVariantsReducedForColour,
  isSomeVariantsSaveForColour,
} from "@/utils/product/promotionUtils";
import { ColourOption } from "./types";
import { createVariantGroupUrlObj } from "@/components/Colours/utils/createVariantGroupUrlObj.ts";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";

type ProductColourInfo = {
  isAvailable: boolean;
  isSelected: boolean;
  allVariantsReduced: boolean;
  allVariantsSave: boolean;
  allVariantsPriceMatched: boolean;
  someVariantsReduced: boolean;
  someVariantsSave: boolean;
  someVariantsPriceMatched: boolean;
  colourSwatch: string;
  url: { pathname: string; query: NextParsedUrlQuery };
};

type ColourToProductColourInfo = Record<string, ProductColourInfo>;

export const transformToColourOptions = (
  product: Product,
  selectedColour: string,
  routerQuery: NextParsedUrlQuery,
  flags: {
    showPromoOnIndicators: boolean;
    ignoreOutOfStockVariantsOnPromo: boolean;
    showPriceMatchedNonPriceEstablishedLabel: boolean;
  },
): ColourOption[] => {
  if (!product.variantGroups) {
    return [];
  }

  const allVariantsReduced = isAllVariantsReduced(product);
  const allVariantsSave = isAllVariantsSave(product);
  const productVariantsPriceMatchNonPriceEstablishedStatus =
    flags.showPriceMatchedNonPriceEstablishedLabel
      ? getProductPriceMatchNonPriceEstablishedStatus(product)
      : "none";
  const allVariantsPriceMatched =
    productVariantsPriceMatchNonPriceEstablishedStatus === "all";

  let allVariantsGroupsReducedOrSaveOrPriceMatched = false;

  const productColoursInfo =
    product.variantGroups.reduce<ColourToProductColourInfo>(
      (list, current: VariantGroup) => {
        const colour = current.value || "";
        const groupVariants = getVariantsForGroup(current, product);

        const isAvailable = groupVariants.some(
          (v) =>
            v.availability?.availableToOrder ||
            v.availability?.unavailableReason === "inStoreOnly",
        );

        const priceMatchNonPriceEstablishedStatusForColour =
          flags.showPriceMatchedNonPriceEstablishedLabel
            ? getPriceMatchNonPriceEstablishedStatusForColour(current)
            : "none";

        list[colour] = {
          isAvailable,
          isSelected: selectedColour === colour,
          colourSwatch: groupVariants[0]?.colourSwatch || "",
          allVariantsReduced: isAllVariantsReducedForColour(current),
          allVariantsSave: isAllVariantsSaveForColour(current),
          allVariantsPriceMatched:
            priceMatchNonPriceEstablishedStatusForColour === "all",
          someVariantsReduced: isSomeVariantsReducedForColour(current),
          someVariantsSave: isSomeVariantsSaveForColour(current),
          someVariantsPriceMatched:
            priceMatchNonPriceEstablishedStatusForColour === "some",
          url: createVariantGroupUrlObj(current, routerQuery),
        };
        return list;
      },
      {},
    );

  if (
    flags.ignoreOutOfStockVariantsOnPromo &&
    !allVariantsSave &&
    !allVariantsReduced &&
    !allVariantsPriceMatched
  ) {
    const availableProductColours = Object.values(productColoursInfo).filter(
      (v) => v.isAvailable,
    );
    allVariantsGroupsReducedOrSaveOrPriceMatched =
      availableProductColours.length > 0 &&
      availableProductColours.every(
        (vg) =>
          vg.someVariantsReduced ||
          vg.someVariantsSave ||
          vg.allVariantsSave ||
          vg.allVariantsReduced ||
          vg.someVariantsPriceMatched ||
          vg.allVariantsPriceMatched,
      );
  }

  return Object.entries(productColoursInfo).map(([colour, productColour]) => {
    const reductionLabel = getReductionLabel(
      productColour,
      allVariantsReduced,
      allVariantsSave,
      allVariantsPriceMatched,
      allVariantsGroupsReducedOrSaveOrPriceMatched,
      { showPromoOnIndicators: flags.showPromoOnIndicators },
    );

    const promoIgnored = reductionLabel === "Ignored";

    return {
      colour,
      showAsAvailable: productColour.isAvailable,
      colourSwatch: productColour.colourSwatch,
      showReduced: promoIgnored ? false : !!reductionLabel,
      reducedLabel: reductionLabel,
      selected: productColour.isSelected,
      url: productColour.url,
      reducedButIgnored: promoIgnored,
    };
  });
};

export const getReductionLabel = (
  productColourInfo: ProductColourInfo,
  allVariantsReduced: boolean,
  allVariantsSave: boolean,
  allVariantsPriceMatchPromo: boolean,
  allVariantsGroupsReducedOrSaveOrPriceMatched: boolean,
  flags: { showPromoOnIndicators: boolean } = { showPromoOnIndicators: false },
) => {
  if (!productColourInfo.isAvailable) {
    return null;
  }

  if (
    allVariantsReduced ||
    allVariantsSave ||
    allVariantsPriceMatchPromo ||
    allVariantsGroupsReducedOrSaveOrPriceMatched
  ) {
    if (flags.showPromoOnIndicators) {
      if (allVariantsReduced) {
        return "Reduced";
      }

      if (allVariantsSave) {
        return "Offer";
      }

      if (allVariantsPriceMatchPromo) {
        return "Offer";
      }

      if (allVariantsGroupsReducedOrSaveOrPriceMatched) {
        return "Offer";
      }
    }
    return "Ignored";
  }

  if (productColourInfo.allVariantsReduced) {
    return "Reduced";
  }

  if (productColourInfo.allVariantsSave) {
    return "Offer";
  }

  if (productColourInfo.allVariantsPriceMatched) {
    return "Offer";
  }

  if (productColourInfo.someVariantsReduced) {
    return "Selected sizes reduced";
  }

  if (productColourInfo.someVariantsSave) {
    return "Selected sizes offer";
  }

  if (productColourInfo.someVariantsPriceMatched) {
    return "Selected sizes offer";
  }

  return null;
};

export const getItemsCountToDisplay = (
  isMobile: boolean,
  colourOptions: ColourOption[],
  selectedVariant: Variant,
): number => {
  const selectedColourIndex = colourOptions.findIndex(
    (colour) => colour.colour === selectedVariant.differentiators?.colour,
  );

  const ceil = isMobile
    ? Math.ceil((selectedColourIndex + 1) / 2) * 2
    : Math.ceil((selectedColourIndex + 1) / 3) * 3;
  if (selectedColourIndex >= 9) return ceil;

  if (isMobile) return 10;

  return 9;
};

export const sortColourOptions = (colours: ColourOption[]) => {
  const colourOptions = [...colours];

  const colourOptionsAvailable = colourOptions.filter(
    (colourOption) => colourOption.showAsAvailable,
  );

  const colourOptionsNotAvailable = colourOptions.filter(
    (colourOption) => !colourOption.showAsAvailable,
  );

  return [...colourOptionsAvailable, ...colourOptionsNotAvailable];
};

export const getVariantsForGroup = (
  variantGroup: VariantGroup,
  product: Product,
) => {
  const groupVariantIds = variantGroup.variants.map((v) => v.id);
  const allVariants: Variant[] = product.variants;

  return allVariants.filter((v) => groupVariantIds.includes(v.id));
};
